import { AttachApostle } from '@apeiron/apostle'
import {
  ApeironBlueButton,
  ApeironLinkButton,
  MayNull,
  NextImage,
  noForwardProps
} from '@apeiron/temp'
import { ApeironCheckbox, ApeironTooltipInfoButton } from '@apeiron/ui'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, ButtonBase, styled, Typography } from '@mui/material'
import CrossPNG from '@public/icons/icon-remove.png'
import ThinPlusSVG from '@public/icons/icon-thin-plus.svg'
import ApostleCard from '@src/components/apostles/card/ApostleCard'
import { ROUTE } from '@src/constants/route'
import { useApostleAttachContext } from '@src/contexts/apostle/ApostleAttachContext'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  justify-content: center;
  position: relative;
  width: 100%;
`

const IconPlus = styled(NextImage)`
  aspect-ratio: 1;
  width: 25%;
`

const CrossIcon = styled(CloseIcon)`
  aspect-ratio: 1;
  font-size: 100px;
  color: #601818;
  stroke: #601818;
  stroke-width: 1.5;
`

const EmptyCardContainer = styled(Button, noForwardProps)<DisableProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border: 1px solid #ffffff33;
  gap: 2%;
  padding: 10%;
  ${props =>
    props.__plainDisable
      ? ''
      : `
        :disabled {
          filter: brightness(0.5);
          opacity: 0.7;
        }
      `};
`

const CheckboxContainer = styled(Box)`
  align-items: center;
  display: flex;
  gap: 6px;
`

const Checkbox = styled(ApeironCheckbox)`
  align-items: center;
  gap: 6px;
  .Custom-Description {
    color: white;
    font-size: 10px;
    font-weight: 300;
    white-space: nowrap;
  }
`

const ExplainButton = styled(ApeironTooltipInfoButton)`
  .Custom-Button {
    width: 14px;
  }
`

const Explain = styled(Typography)`
  font-size: 14px;
  font-weight: 300;
  line-height: 26px;
  max-width: 300px;
`

const Description = styled(Box)`
  font-size: 12px;
  text-align: center;
  color: white;
  text-transform: none;
  font-weight: 400;
  strong {
    color: #601818;
    font-weight: 700;
  }

  &.grey {
    color: #ffffff80;
  }
`

const BrowseInEmporiumButton = styled(ApeironLinkButton)`
  border: 2px solid #ffffff80;
  border-radius: 4px;
  padding: 6px;
  color: white;
  text-transform: none;
  font-weight: 400;
  font-size: 12px;
`

const Card = styled(ApostleCard)`
  width: 100%;
`

const ChangeButton = styled(ApeironBlueButton)`
  border-radius: 5px;
  font-weight: 400;
  height: 34px;
  margin-bottom: auto;
  width: 100%;
`

const NewBox = styled(Typography)`
  background-color: rgba(255, 0, 168, 0.4);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 300;
  margin-top: -16px;
  padding-top: 9px;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
`

const CrossImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 100%;
`

const CrossButton = styled(ButtonBase)`
  position: absolute;
  width: 25%;
  ${props => props.theme.breakpoints.up('res1024')} {
    top: 0px;
    right: -14px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    top: -14px;
    right: -14px;
  }
`

const EmptyCard: FC<{
  marketplaceUrl: string
  disabled?: boolean
  onClick: () => void
  isNew?: boolean
}> = ({ marketplaceUrl, disabled, onClick, isNew }) => {
  const { t } = useTranslation()

  return (
    <EmptyCardContainer disabled={disabled} onClick={onClick}>
      <IconPlus src={ThinPlusSVG} />
      <Description>{t('sanctuary.manage_apostle.attach_apostle')}</Description>
      <BrowseInEmporiumButton target='_blank' url={marketplaceUrl}>
        {t('star.attachment.slot.button.browse_marketplace')}
      </BrowseInEmporiumButton>
      {isNew && <NewBox>{t('common.button.new')}</NewBox>}
    </EmptyCardContainer>
  )
}

const LockedCard: FC<{ lockedAge: string }> = ({ lockedAge }) => {
  const { t } = useTranslation()

  return (
    <EmptyCardContainer __plainDisable disabled>
      <CrossIcon />
      <Description>
        <strong>{t('apostle.locked')} </strong>
      </Description>
      <Description className='grey'>
        {t('sanctuary.manage_apostle.unlock_age', {
          lockedAge
        })}
      </Description>
    </EmptyCardContainer>
  )
}

const ShowApostleCard: FC<{
  apostle: AttachApostle
  isNew?: boolean
  onChangeClick: () => void
  onCheckboxChange: () => void
  onRemoveClick: () => void
}> = ({
  apostle,
  isNew = false,
  onChangeClick,
  onCheckboxChange,
  onRemoveClick
}) => {
  const { t } = useTranslation()

  return (
    <MainContainer>
      <Card apostle={apostle} onClick={onChangeClick} />
      <ChangeButton onClick={onChangeClick}>
        {t('common.button.change')}
      </ChangeButton>
      {isNew && (
        <>
          <NewBox>{t('common.button.new')}</NewBox>
          <CheckboxContainer>
            <Checkbox
              checked={apostle.materialize}
              onChange={onCheckboxChange}
              description={t('sanctuary.manage_apostle.as_material')}
            />
            <ExplainButton tooltipId='material-explain'>
              <Explain>
                {t('sanctuary.manage_apostle.as_material_explain')}
              </Explain>
            </ExplainButton>
          </CheckboxContainer>
        </>
      )}
      <CrossButton onClick={onRemoveClick}>
        <CrossImage src={CrossPNG} />
      </CrossButton>
    </MainContainer>
  )
}

const ApostleAttachCard: FC<Props> = (props: Props) => {
  const {
    apostle,
    className,
    lockedAge,
    planetId,
    onAddClick,
    onChangeClick,
    onRemoveClick,
    onCheckboxChange
  } = props

  const { checkIsNew } = useApostleAttachContext()

  const marketplaceUrl = queryString.stringifyUrl({
    url: ROUTE.MARKETPLACE_APOSTLE_LIST
  })

  const isNew = useMemo(() => {
    return checkIsNew(planetId, apostle?.id)
  }, [apostle?.id, checkIsNew, planetId])

  const handleOnAddClick = useCallback(() => {
    if (onAddClick) {
      onAddClick()
    }
  }, [onAddClick])

  const handleOnChangeClick = useCallback(() => {
    if (R.isNotNil(apostle) && onChangeClick) {
      onChangeClick(apostle.id)
    }
  }, [apostle, onChangeClick])

  const handleOnRemoveClick = useCallback(() => {
    if (R.isNotNil(apostle) && onRemoveClick) {
      onRemoveClick(apostle.id)
    }
  }, [apostle, onRemoveClick])

  const handleOnCheckboxChange = useCallback(() => {
    if (R.isNotNil(apostle) && onCheckboxChange) {
      onCheckboxChange(apostle.id)
    }
  }, [apostle, onCheckboxChange])

  return (
    <Box className={className}>
      {apostle ? (
        <ShowApostleCard
          apostle={apostle}
          isNew={isNew}
          onChangeClick={handleOnChangeClick}
          onRemoveClick={handleOnRemoveClick}
          onCheckboxChange={handleOnCheckboxChange}
        />
      ) : lockedAge ? (
        <LockedCard lockedAge={lockedAge} />
      ) : (
        <EmptyCard
          marketplaceUrl={marketplaceUrl}
          disabled={planetId === ''}
          onClick={handleOnAddClick}
          isNew={isNew}
        />
      )}
    </Box>
  )
}

type DisableProps = {
  __plainDisable?: boolean
}

type Props = {
  apostle?: MayNull<AttachApostle>
  className?: string
  lockedAge?: string
  planetId: string
  onAddClick?: () => void
  onChangeClick?: (apostleId: string) => void
  onRemoveClick?: (apostleId: string) => void
  onCheckboxChange?: (apostleId: string) => void
}

export default ApostleAttachCard
